import { useMemo } from 'react';

import { useAbility } from '@cast/shared-ability-data-access';
import {
  useCanCreatePolicy,
  useCanEditPolicy,
} from '@cast/split-me/features/cluster/workload-optimization';

import { useIsOpsPilotEnabled } from '@cast/features-organization-shared-opspilot';
import { useKentAbilities } from '@cast/split-me/features/kent';
import { useCluster } from '@cast/shared-cluster-context-data-access';

// This hook is the single source of truth for all permission checks required by
// the API calls made inside useWorkloadAutoscaler, useNodeRebalancePlan,
// useRebalancingSchedule, and useEvictorConfig. Add new permission gates here.
export const useCanUseOptimizationWizard = () => {
  const ability = useAbility();
  const { isKentManaged } = useCluster();

  const {
    canEditRebalancingSchedule: canEditKentRebalancingSchedule,
    canCreateRebalancingSchedule: canCreateKentRebalancingSchedule,
    canViewFeatures: canViewKentFeatures,
    canUpdateFeatures: canUpdateKentFeatures,
  } = useKentAbilities();

  const [
    abilityToViewAutopilot,
    canViewRebalancer,
    canViewAutoscaler,
    canUpdateAutoscaler,
    canCreateRebalancer,
    canUpdateRebalancer,
    canCreateSchedule,
    canUpdateSchedule,
  ] = ability.canMany([
    ['view', 'ClusterWorkloadAutopilot'],
    ['view', 'ClusterRebalancer'],
    ['view', 'ClusterAutoscaler'],
    ['update', 'ClusterAutoscaler'],
    ['create', 'ClusterRebalancer'],
    ['update', 'ClusterRebalancer'],
    ['create', 'OrganizationRebalanceSchedules'],
    ['update', 'OrganizationRebalanceSchedules'],
  ]);

  const { canCreatePolicy } = useCanCreatePolicy();
  const { canEdit } = useCanEditPolicy();

  const { opsPilotStatus } = useIsOpsPilotEnabled();

  const canViewAutopilot = useMemo(
    () =>
      abilityToViewAutopilot &&
      canViewRebalancer &&
      canViewAutoscaler &&
      opsPilotStatus !== 'disabled',
    [
      abilityToViewAutopilot,
      canViewRebalancer,
      canViewAutoscaler,
      opsPilotStatus,
    ]
  );

  const canApplyWorkloadOptimization =
    canViewAutopilot && canCreatePolicy && canEdit;

  // Gates the node autoscaling feature checkbox — disabled when user lacks
  // write permissions for rebalancer and autoscaler operations.
  const canUseNodeAutoscaling =
    canCreateRebalancer && canUpdateRebalancer && canUpdateAutoscaler;

  const canApplyNodeRebalancing =
    canViewAutopilot &&
    canViewRebalancer &&
    canViewAutoscaler &&
    canUpdateAutoscaler &&
    canCreateRebalancer &&
    canUpdateRebalancer;

  const canApplySchedule =
    canViewAutopilot && isKentManaged
      ? canCreateKentRebalancingSchedule && canEditKentRebalancingSchedule
      : canViewRebalancer &&
        canCreateRebalancer &&
        canUpdateRebalancer &&
        canCreateSchedule &&
        canUpdateSchedule;

  const canApplyEvictor =
    canViewAutopilot &&
    (isKentManaged
      ? // Kent configures consolidation (its name for the evictor) through the features API
        canViewKentFeatures && canUpdateKentFeatures
      : canViewAutoscaler && canUpdateAutoscaler);

  const canApplyOptimization =
    canApplyWorkloadOptimization &&
    canApplyNodeRebalancing &&
    canApplySchedule &&
    canApplyEvictor;

  return {
    canViewAutopilot,
    canUseNodeAutoscaling,
    canApplyWorkloadOptimization,
    canApplyNodeRebalancing,
    canApplySchedule,
    canApplyEvictor,
    canApplyOptimization,
  };
};
